import storageDefaults from 'decorators/storageDefaults';
import storageIgnore from 'decorators/storageIgnore';
import { RepliesViews } from 'enums/replies/views';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { CollectionDataSource } from 'managers/datasources/collection';
import events from 'managers/events';
import SignalREventsManager from 'managers/signalR/events';
import { IWCCStorageManager } from 'managers/iStorage';
import { EffectsContainer } from 'mixins/withEffects';
import { JSONTopicThread, TopicThread } from 'models/topicThread';
import { ServicesContext } from 'services/context';
import TopicThreadsEventsManager from '../threads/eventsProcessor';

export interface CommentRepliesManagerConfig {
    topicId: string
    commentId: string
    view: RepliesViews
    isEmpty: boolean
}

@injectable()
@storageDefaults(<Partial<CommentRepliesManagerConfig>>{ view: 'all' })
@storageIgnore<CommentRepliesManagerConfig>('isEmpty')
export default class CommentRepliesManager {
    private source: CollectionDataSource<TopicThread, 'id'>

    private topicId: string
    private commentId: string
    private view: RepliesViews

    replies = ko.pureComputed(() => this.source.list());

    busy = ko.pureComputed(() => this.source.busy());
    loading = ko.pureComputed(() => this.source.busy() && this.source.requestsProcessed() === 0);
    updating = ko.pureComputed(() => this.source.busy() && this.source.requestsProcessed() > 0);

    constructor(
        @inject(wccModules.managerConfig) { topicId, commentId, view, isEmpty }: CommentRepliesManagerConfig,
        @inject(wccModules.servicesContext) private ctx: ServicesContext,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.signalREvents) signalREvents: SignalREventsManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.topicId = topicId;
        this.commentId = commentId;
        this.view = view;

        this.source = effects.register(new CollectionDataSource({
            key: 'id',
            load: this.load.bind(this),
            update: this.update.bind(this),
            mapper: jsonThread => new TopicThread(jsonThread),
            isEmpty: isEmpty
        }));

        effects.register(new TopicThreadsEventsManager({
            topicId: this.topicId,
            source: this.source
        }, ctx, storage, signalREvents));

        ctx.commentsService.events.newTopicReply.on(this.onNewReply.bind(this));        

        effects.register([
            events.replyAdded.on(this.onLocalReplyAdded.bind(this)),
            events.replyUpdated.on(this.onLocalReplyUpdated.bind(this)),
            events.replyDeleted.on(this.onLocalReplyDeleted.bind(this))
        ]);        
    }

    private load() {
        return this.ctx
            .commentsService
            .queries
            .commentReplies(this.commentId)
            .addArgs({ '$orderby': 'TopicThreadId asc', 'view': this.view })
            .background()
            .toArrayPost({ '': this.replies().map(r => r.id()) });
    }

    private async update(reply: TopicThread) {
        reply.update(await this.getThread(reply.id()));
    }

    private async getThread(threadId?: string) {
        if (threadId)
            return await this.ctx.commentsService.queries.thread(threadId).important().firstOrDefault();
        else
            throw new Error(messages.InvalidRequest);
    }

    private onNewReply(threadId: string, parenThreadId: string, topicId: string) {
        if (this.topicId === topicId && this.commentId === parenThreadId)
            this.source.load(true);
    }    

    private onLocalReplyAdded(topicId: string, commentId: string, jsonThread: JSONTopicThread) {
        if (this.topicId === topicId && this.commentId === commentId) {
            this.source.add(jsonThread);
            _(this.replies()).each(reply => reply.isInToDoList(false));
        }
    }

    private onLocalReplyUpdated(topicId: string, commentId: string, replyId: string, jsonThread: JSONTopicThread) {
        if (this.topicId === topicId && this.commentId === commentId)
            this.source.findSync(replyId, reply => reply.update(jsonThread));
    }

    private onLocalReplyDeleted(topicId: string, commentId: string, replyId: string) {
        if (this.topicId === topicId && this.commentId === commentId)
            this.source.findSync(replyId, reply => reply.isDeleted(true));
    }    
}